<template>
  <div class="flex flex-col">
    <div v-if="!loading && campaign">
      <!-- Header -->
      <div class="card">
        <card-header :title="campaign.subject">
          <template #badge>
            <status-badge :status="campaign.status" />
          </template>
          <template #actions>
            <router-link
              v-if="campaign.status === 'draft'"
              :to="{ name: 'massEmailsForm', params: { id: campaign.id } }"
              class="secondary-button"
            >
              Editar
            </router-link>

            <button
              v-if="campaign.status === 'draft' || campaign.status === 'scheduled'"
              @click="sendCampaign"
              :disabled="loadingSend"
              class="primary-button"
            >
              {{ campaign.status === 'scheduled' ? 'Enviar Ahora' : 'Enviar Campaña' }}
            </button>

            <button
              v-if="campaign.status === 'scheduled'"
              @click="cancelCampaign"
              :disabled="loadingCancel"
              class="delete-button"
            >
              Cancelar Programación
            </button>
          </template>
        </card-header>

        <p class="text-sm text-gray-500">ID: #{{ campaign.id }}</p>

        <section-header title="Información de la Campaña" />

        <div class="detail-grid">
          <detail-field label="Audiencia" :value="formatAudienceType(campaign.audience_type)" />
          <detail-field label="Total Destinatarios" :value="campaign.total_recipients" :prominent="true" />
          <detail-field label="Creado por" :value="campaign.creator?.name || 'N/A'" />
          <detail-field label="Fecha de Creación" :value="formatDate(campaign.created_at)" />
          <detail-field v-if="campaign.scheduled_at" label="Programado para" :value="formatDate(campaign.scheduled_at)" />
          <detail-field v-if="campaign.sent_at" label="Enviado" :value="formatDate(campaign.sent_at)" />
        </div>

        <div class="mt-4 pt-4 border-t">
          <p class="detail-label mb-2">Contenido del Email</p>
          <div class="bg-gray-50 p-4 rounded-lg" v-html="campaign.content"></div>
        </div>
      </div>

      <!-- Statistics -->
      <div v-if="campaign.status !== 'draft'" class="mt-6">
        <section-header title="Estadísticas" />

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Sent -->
          <div class="card bg-success-50 border border-success-200">
            <p class="stat-label text-success-700">Enviados</p>
            <p class="stat-value text-success-600">{{ campaign.sent_count }}</p>
            <p class="text-xs text-success-600 mt-1">
              de {{ campaign.total_recipients }} total
            </p>
          </div>

          <!-- Opens -->
          <div class="card bg-primary-50 border border-primary-200">
            <p class="stat-label text-primary-700">Aperturas</p>
            <p class="stat-value text-primary-600">{{ campaign.opened_count }}</p>
            <p class="text-xs text-primary-600 mt-1">
              {{ statistics?.engagement?.open_rate || 0 }}% tasa de apertura
            </p>
          </div>

          <!-- Clicks -->
          <div class="card bg-quaternary-50 border border-quaternary-200">
            <p class="stat-label text-quaternary-700">Clics</p>
            <p class="stat-value text-quaternary-600">{{ campaign.clicked_count }}</p>
            <p class="text-xs text-quaternary-600 mt-1">
              {{ statistics?.engagement?.click_rate || 0 }}% tasa de clics
            </p>
          </div>

          <!-- Failed -->
          <div class="card bg-error-50 border border-error-200">
            <p class="stat-label text-error-700">Fallidos</p>
            <p class="stat-value text-error-600">{{ campaign.failed_count }}</p>
            <p class="text-xs text-error-600 mt-1">
              {{ campaign.bounced_count }} rebotados
            </p>
          </div>
        </div>

        <!-- Additional Stats -->
        <div v-if="statistics" class="card mt-4">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
            <div>
              <p class="detail-label mb-2">Estado de Envío</p>
              <div class="space-y-1">
                <div class="flex justify-between">
                  <span>Pendientes:</span>
                  <span class="font-semibold">{{ statistics.overview?.pending || 0 }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Enviados:</span>
                  <span class="font-semibold text-success-600">{{ statistics.overview?.sent || 0 }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Fallidos:</span>
                  <span class="font-semibold text-error-600">{{ statistics.overview?.failed || 0 }}</span>
                </div>
              </div>
            </div>

            <div>
              <p class="detail-label mb-2">Engagement</p>
              <div class="space-y-1">
                <div class="flex justify-between">
                  <span>Apertura:</span>
                  <span class="font-semibold">{{ statistics.engagement?.open_rate || 0 }}%</span>
                </div>
                <div class="flex justify-between">
                  <span>Clics:</span>
                  <span class="font-semibold">{{ statistics.engagement?.click_rate || 0 }}%</span>
                </div>
              </div>
            </div>

            <div>
              <p class="detail-label mb-2">Problemas</p>
              <div class="space-y-1">
                <div class="flex justify-between">
                  <span>Rebotados:</span>
                  <span class="font-semibold">{{ statistics.issues?.bounced || 0 }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Bajas:</span>
                  <span class="font-semibold">{{ statistics.issues?.unsubscribed || 0 }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Tasa rebote:</span>
                  <span class="font-semibold">{{ statistics.issues?.bounce_rate || 0 }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recipients List -->
      <div v-if="campaign.status !== 'draft'" class="mt-6">
        <div class="flex justify-between items-center mb-4">
          <section-header title="Destinatarios" />

          <div class="flex space-x-2">
            <button
              v-for="status in recipientStatuses"
              :key="status.value"
              @click="filterRecipients(status.value)"
              :class="[
                'px-3 py-1 rounded-full text-xs font-medium smooth',
                selectedRecipientStatus === status.value
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              ]"
            >
              {{ status.label }}
            </button>
          </div>
        </div>

        <div class="card">
          <div v-if="recipients.length > 0" class="overflow-x-auto">
            <table class="w-full table-auto">
              <thead>
                <tr class="bg-primary-100 text-left">
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Email</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Enviado</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Abierto</th>
                  <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Clicado</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="recipient in recipients"
                  :key="recipient.id"
                  class="table-row text-xs"
                >
                  <td class="table-cell">
                    <router-link
                      v-if="recipient.client"
                      :to="{ name: 'clientsDetail', params: { id: recipient.client.id } }"
                      class="text-primary-500 hover:text-primary-600"
                    >
                      {{ recipient.client.name }} {{ recipient.client.last_name }}
                    </router-link>
                    <span v-else class="text-gray-500">N/A</span>
                  </td>
                  <td class="table-cell text-gray-600">{{ recipient.email }}</td>
                  <td class="table-cell">
                    <status-badge :status="recipient.status" :label="formatRecipientStatus(recipient.status)" />
                  </td>
                  <td class="table-cell text-gray-600">{{ recipient.sent_at ? formatDateTime(recipient.sent_at) : '-' }}</td>
                  <td class="table-cell text-gray-600">{{ recipient.opened_at ? formatDateTime(recipient.opened_at) : '-' }}</td>
                  <td class="table-cell text-gray-600">{{ recipient.clicked_at ? formatDateTime(recipient.clicked_at) : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8 text-gray-500">
            No hay destinatarios {{ selectedRecipientStatus ? 'con este estado' : '' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="card mt-6 py-12 text-center">
      <div class="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
      <p class="text-gray-500 mt-4">Cargando campaña...</p>
    </div>

    <!-- Confirmation modals -->
    <confirmation-modal
      v-if="showSendModal"
      title="Confirmar Envío"
      :message="`¿Estás seguro de enviar esta campaña a ${campaign?.total_recipients} destinatarios? Esta acción no se puede deshacer.`"
      confirm-text="Enviar Ahora"
      :loading="loadingSend"
      @close="showSendModal = false"
      @confirm="confirmSend"
    />

    <confirmation-modal
      v-if="showCancelModal"
      title="Cancelar Programación"
      message="¿Estás seguro de cancelar el envío programado? La campaña volverá al estado de borrador."
      confirm-text="Cancelar Programación"
      :loading="loadingCancel"
      @close="showCancelModal = false"
      @confirm="confirmCancel"
    />
  </div>
</template>

<script>
import api from '@/api';
import ConfirmationModal from '@/components/ui/ConfirmationModal.vue';

export default {
  name: 'MassEmailsDetail',

  components: {
    ConfirmationModal,
  },

  data() {
    return {
      campaign: null,
      statistics: null,
      recipients: [],
      loading: false,
      loadingSend: false,
      loadingCancel: false,
      showSendModal: false,
      showCancelModal: false,
      selectedRecipientStatus: null,
      recipientStatuses: [
        { value: null, label: 'Todos' },
        { value: 'pending', label: 'Pendientes' },
        { value: 'sent', label: 'Enviados' },
        { value: 'opened', label: 'Abiertos' },
        { value: 'clicked', label: 'Clicados' },
        { value: 'failed', label: 'Fallidos' },
      ],
    };
  },

  async mounted() {
    await this.loadCampaign();
  },

  methods: {
    async loadCampaign() {
      this.loading = true;
      try {
        this.campaign = await api.massEmails.getById(this.$route.params.id);
        this.$store.commit('setBreadcrumbLabel', {
          routeName: this.$route.name,
          label: this.campaign.subject,
        });

        if (this.campaign.status !== 'draft') {
          await this.loadStatistics();
          await this.loadRecipients();
        }
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al cargar campaña');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async loadStatistics() {
      try {
        this.statistics = await api.massEmails.getStatistics(this.campaign.id);
      } catch (error) {
        console.error('Error loading statistics:', error);
      }
    },

    async loadRecipients() {
      try {
        const params = {};
        if (this.selectedRecipientStatus) {
          params.status = this.selectedRecipientStatus;
        }

        const response = await api.massEmails.getRecipients(this.campaign.id, params);
        this.recipients = response.data;
      } catch (error) {
        console.error('Error loading recipients:', error);
      }
    },

    filterRecipients(status) {
      this.selectedRecipientStatus = status;
      this.loadRecipients();
    },

    sendCampaign() {
      this.showSendModal = true;
    },

    async confirmSend() {
      this.loadingSend = true;
      try {
        await api.massEmails.send(this.campaign.id);
        this.$store.dispatch('successSnack', 'Campaña enviándose en segundo plano');
        await this.loadCampaign();
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al enviar campaña');
        console.error(error);
      } finally {
        this.loadingSend = false;
        this.showSendModal = false;
      }
    },

    cancelCampaign() {
      this.showCancelModal = true;
    },

    async confirmCancel() {
      this.loadingCancel = true;
      try {
        await api.massEmails.cancel(this.campaign.id);
        this.$store.dispatch('successSnack', 'Programación cancelada');
        await this.loadCampaign();
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al cancelar programación');
        console.error(error);
      } finally {
        this.loadingCancel = false;
        this.showCancelModal = false;
      }
    },

    formatAudienceType(type) {
      const types = {
        cats: 'Solo Gatos',
        dogs: 'Solo Perros',
        both: 'Gatos y Perros',
        pending_quotes: 'Cotizaciones Pendientes',
        cancelled_quotes: 'Cotizaciones Canceladas',
        active_quotes: 'Cotizaciones Activas',
        everyone: 'Todos',
      };
      return types[type] || type;
    },

    formatStatus(status) {
      const statuses = {
        draft: 'Borrador',
        scheduled: 'Programada',
        sending: 'Enviando',
        sent: 'Enviada',
        failed: 'Fallida',
      };
      return statuses[status] || status;
    },

    formatRecipientStatus(status) {
      const statuses = {
        pending: 'Pendiente',
        sent: 'Enviado',
        opened: 'Abierto',
        clicked: 'Clicado',
        failed: 'Fallido',
        bounced: 'Rebotado',
        unsubscribed: 'Baja',
      };
      return statuses[status] || status;
    },

    getStatusClass(status) {
      const classes = {
        draft: 'px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium',
        scheduled: 'px-3 py-1 rounded-full bg-tertiary-100 text-tertiary-700 text-sm font-medium',
        sending: 'px-3 py-1 rounded-full bg-quaternary-100 text-quaternary-700 text-sm font-medium',
        sent: 'px-3 py-1 rounded-full bg-success-100 text-success-700 text-sm font-medium',
        failed: 'px-3 py-1 rounded-full bg-error-100 text-error-700 text-sm font-medium',
      };
      return classes[status] || classes.draft;
    },

    getRecipientStatusClass(status) {
      const classes = {
        pending: 'px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs',
        sent: 'px-2 py-0.5 rounded-full bg-success-100 text-success-700 text-xs',
        opened: 'px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs',
        clicked: 'px-2 py-0.5 rounded-full bg-quaternary-100 text-quaternary-700 text-xs',
        failed: 'px-2 py-0.5 rounded-full bg-error-100 text-error-700 text-xs',
        bounced: 'px-2 py-0.5 rounded-full bg-error-100 text-error-700 text-xs',
        unsubscribed: 'px-2 py-0.5 rounded-full bg-gray-200 text-gray-800 text-xs',
      };
      return classes[status] || classes.pending;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('es-MX', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>
